import React from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import { useTheme } from 'styled-components';
import { RightSideCards } from '../LeadDetails/LeadDetailsEdit/leadDetailsEdit.styles';
import useCommunitySettings from '../../hooks/useCommunitySettings';
import CommunitySettingsSection from './CommunitySettingsSection';
import OfficeHoursDisplay from './OfficeHoursDisplay';

// Helper component for displaying settings rows
const SettingsRow = ({ label, value, isLink = false }) => {
  const theme = useTheme();

  if (!value) return null;

  return (
    <Box sx={{ display: 'flex', py: 0.5 }}>
      <Typography
        sx={{
          fontSize: '12px',
          color: '#333',
          minWidth: '120px',
          fontWeight: 500
        }}
      >
        {label}:
      </Typography>
      {isLink ? (
        <Typography
          component="a"
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: '12px',
            color: theme?.text?.link || '#5267FF',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            wordBreak: 'break-all'
          }}
        >
          {value}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontSize: '12px',
            color: theme?.text?.primary,
            wordBreak: 'break-word'
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

// Helper to format tour type for display
const formatTourType = type => {
  const typeMap = {
    in_person: 'In-Person',
    virtual: 'Virtual',
    self_guided: 'Self-Guided'
  };
  return typeMap[type] || type;
};

const CommunitySettings = props => {
  const { apartmentId } = props;
  const theme = useTheme();

  const {
    data: communityData,
    isLoading,
    isError
  } = useCommunitySettings({
    communityId: apartmentId
  });

  const hasData = communityData && Object.keys(communityData).length > 0;

  return (
    <RightSideCards className="right-side-cards">
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography className="card-title" sx={{ color: theme?.text?.heading }}>
          Community Settings
        </Typography>
        {communityData?.isLive && (
          <Chip
            label="Live"
            size="small"
            color="success"
            sx={{ fontSize: '10px', height: '20px' }}
          />
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.primary
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            Loading community settings
          </Typography>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.error || '#ff4d4f',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Failed to load community settings
          </Typography>
          <Typography sx={{ fontSize: '12px', mt: 0.625, opacity: 0.8 }}>
            Please try refreshing the page or contact support if the issue
            persists.
          </Typography>
        </Box>
      ) : hasData ? (
        <Box>
          {/* Basic Information Section */}
          <CommunitySettingsSection title="Basic Information">
            <SettingsRow label="Name" value={communityData.name} />
            <SettingsRow label="Website" value={communityData.website} isLink />
            <SettingsRow label="Phone" value={communityData.phone} />
            <SettingsRow label="Email" value={communityData.email} />
            <SettingsRow
              label="Webhook Email"
              value={communityData.webhookEmail}
            />
          </CommunitySettingsSection>

          {/* Address Section */}
          {communityData.address && (
            <CommunitySettingsSection title="Address">
              <SettingsRow
                label="Street"
                value={communityData.address.streetAddress}
              />
              <SettingsRow
                label="City, State"
                value={`${communityData.address.city || ''}${
                  communityData.address.city && communityData.address.stateShort
                    ? ', '
                    : ''
                }${communityData.address.stateShort || ''}`}
              />
              <SettingsRow
                label="Postal Code"
                value={communityData.address.postalCode}
              />
            </CommunitySettingsSection>
          )}

          {/* Units Section */}
          <CommunitySettingsSection title="Units">
            <SettingsRow label="Total Units" value={communityData.totalUnits} />
            <SettingsRow
              label="Available Units"
              value={communityData.availableUnits}
            />
          </CommunitySettingsSection>

          {/* Tour Types Section */}
          {communityData.tourTypes && communityData.tourTypes.length > 0 && (
            <CommunitySettingsSection title="Tour Types">
              <Box
                sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
              >
                {communityData.tourTypes.map(type => (
                  <Chip
                    key={type}
                    label={formatTourType(type)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '11px' }}
                  />
                ))}
              </Box>
            </CommunitySettingsSection>
          )}

          {/* Office Hours Section */}
          {communityData.officeHours &&
            communityData.officeHours.length > 0 && (
              <CommunitySettingsSection title="Office Hours">
                <OfficeHoursDisplay officeHours={communityData.officeHours} />
              </CommunitySettingsSection>
            )}

          {/* Links Section */}
          <CommunitySettingsSection title="Links">
            <SettingsRow
              label="Application Link"
              value={communityData.applicationLink}
              isLink
            />
            <SettingsRow
              label="Self-Guided Tour"
              value={communityData.selfGuidedTourLink}
              isLink
            />
            <SettingsRow
              label="Parser Email"
              value={communityData.parserEmail}
            />
          </CommunitySettingsSection>

          {/* Section 8 Response */}
          {communityData.section8Response && (
            <CommunitySettingsSection title="Section 8 Response">
              <Typography
                sx={{
                  fontSize: '12px',
                  color: theme?.text?.primary,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {communityData.section8Response}
              </Typography>
            </CommunitySettingsSection>
          )}
        </Box>
      ) : (
        <Typography
          sx={{ fontSize: '14px', color: theme?.text?.primary, mt: 1.25 }}
        >
          No community settings available.
        </Typography>
      )}
    </RightSideCards>
  );
};

export default CommunitySettings;
